import React, { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  BarChart3,
  Settings,
  CreditCard,
  Bot,
  Zap,
  Shield,
  HelpCircle,
  Crown,
  Database,
  Brain,
  Network,
  Tags
} from 'lucide-react'
import { motion } from 'framer-motion'

import Logo from '../Common/Logo'
import AuthAPI from '../../services/auth'

const navigation = [
  {
    name: 'Dashboard',
    href: '/app',
    icon: LayoutDashboard,
    description: 'Classification overview and real-time metrics'
  },
  {
    name: 'Integrations',
    href: '/app/integrations',
    icon: Network,
    description: 'Webhooks, API & automation'
  },
  {
    name: 'AI Optimization',
    href: '/app/ai-optimization',
    icon: Brain,
    description: 'Improve AI accuracy with custom prompts'
  },
  {
    name: 'Settings',
    href: '/app/settings',
    icon: Settings,
    description: 'System configuration and categories'
  },
  {
    name: 'Category Tuning',
    href: '/app/category-tuning',
    icon: Tags,
    description: 'Custom categories & tag mapping'
  },
  {
    name: 'Billing',
    href: '/app/billing',
    icon: CreditCard,
    description: 'Usage tracking and payments'
  },
]

const adminNavigation = [
  {
    name: 'Admin Dashboard',
    href: '/app/admin',
    icon: Crown,
    description: 'System overview and monitoring'
  },
  {
    name: 'User Management',
    href: '/app/admin/users',
    icon: Users,
    description: 'Manage user accounts and permissions'
  },
  {
    name: 'System Config',
    href: '/app/admin/system',
    icon: Database,
    description: 'Global system configuration'
  },
]

const features = [
  {
    name: 'AI-Powered',
    icon: Bot,
    description: 'AI-powered classification'
  },
  {
    name: 'Lightning Fast',
    icon: Zap,
    description: 'Fast global response'
  },
  {
    name: 'Enterprise Grade',
    icon: Shield,
    description: 'Security & compliance'
  },
]

const Sidebar = () => {
  const location = useLocation()
  const [user, setUser] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await AuthAPI.getCurrentUser()
        setUser(userData)
        setIsAdmin(userData?.email === 'nigelcannings@gmail.com')
      } catch (error) {
        console.error('Failed to load user data in Sidebar:', error)
      }
    }
    loadUserData()
  }, [])

  return (
    <div className="flex flex-col h-full bg-white border-r border-secondary-200 shadow-sm">
      {/* Logo section */}
      <div className="flex items-center h-16 px-6 border-b border-secondary-200">
        <Logo />
      </div>

      {/* Admin Notice */}
      {isAdmin && (
        <div className="px-4 py-3 bg-orange-50 border-b border-orange-200">
          <div className="flex items-center space-x-2 text-orange-800">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">Admin Access</span>
          </div>
          <p className="text-xs text-orange-600 mt-1">
            You have administrative privileges in Triage Genius
          </p>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {/* Regular Navigation */}
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || 
            (item.href !== '/app' && location.pathname.startsWith(item.href))
          
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive: linkActive }) => {
                const active = linkActive || isActive
                return `
                  group relative flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                  ${active 
                    ? 'bg-primary-50 text-primary-700 shadow-sm' 
                    : 'text-secondary-700 hover:bg-secondary-50 hover:text-secondary-900'
                  }
                `
              }}
            >
              {({ isActive: linkActive }) => {
                const active = linkActive || isActive
                return (
                  <>
                    {/* Active indicator */}
                    {active && (
                      <motion.div
                        layoutId="sidebar-active"
                        className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full"
                        initial={false}
                        transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                      />
                    )}
                    
                    <item.icon className={`
                      mr-3 h-5 w-5 transition-colors
                      ${active ? 'text-primary-600' : 'text-secondary-400 group-hover:text-secondary-600'}
                    `} />
                    
                    <div className="flex-1 min-w-0">
                      <span className="block truncate">
                        {item.name}
                      </span>
                      <span className={`
                        block text-xs truncate mt-0.5
                        ${active ? 'text-primary-500' : 'text-secondary-500'}
                      `}>
                        {item.description}
                      </span>
                    </div>
                  </>
                )
              }}
            </NavLink>
          )
        })}

        {/* Admin Navigation */}
        {isAdmin && (
          <>
            <div className="pt-4 pb-2">
              <div className="border-t border-secondary-200"></div>
              <h3 className="px-3 mt-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                Admin Controls
              </h3>
            </div>
            
            {adminNavigation.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/app' && location.pathname.startsWith(item.href))
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive: linkActive }) => {
                    const active = linkActive || isActive
                    return `
                      group relative flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                      ${active 
                        ? 'bg-orange-50 text-orange-700 shadow-sm' 
                        : 'text-secondary-700 hover:bg-secondary-50 hover:text-secondary-900'
                      }
                    `
                  }}
                >
                  {({ isActive: linkActive }) => {
                    const active = linkActive || isActive
                    return (
                      <>
                        {/* Active indicator */}
                        {active && (
                          <motion.div
                            layoutId="sidebar-active-admin"
                            className="absolute left-0 top-0 bottom-0 w-1 bg-orange-600 rounded-r-full"
                            initial={false}
                            transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                          />
                        )}
                        
                        <item.icon className={`
                          mr-3 h-5 w-5 transition-colors
                          ${active ? 'text-orange-600' : 'text-secondary-400 group-hover:text-secondary-600'}
                        `} />
                        
                        <div className="flex-1 min-w-0">
                          <span className="block truncate">
                            {item.name}
                          </span>
                          <span className={`
                            block text-xs truncate mt-0.5
                            ${active ? 'text-orange-500' : 'text-secondary-500'}
                          `}>
                            {item.description}
                          </span>
                        </div>
                      </>
                    )
                  }}
                </NavLink>
              )
            })}
          </>
        )}
      </nav>

      {/* Features showcase */}
      <div className="px-4 py-6 border-t border-secondary-200">
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-secondary-500 uppercase tracking-wider">
            Key Features
          </h3>
          
          {features.map((feature) => (
            <div key={feature.name} className="flex items-center text-xs text-secondary-600">
              <feature.icon className="h-4 w-4 mr-2 text-secondary-400" />
              <div>
                <div className="font-medium">{feature.name}</div>
                <div className="text-secondary-500">{feature.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Help section */}
      <div className="px-4 pb-6">
        <a
          href="mailto:support@triagegenius.com"
          className="w-full flex items-center px-3 py-2 text-sm text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50 rounded-lg transition-colors"
        >
          <HelpCircle className="h-4 w-4 mr-2" />
          Help & Support
        </a>
      </div>
    </div>
  )
}

export default Sidebar